import React, { useState, useEffect, useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

export default function DateTimePicker({
  date,
  label,
  minDate,
  maxDate,
  handleDateChange,
  extraError,
  extraErrorMessage,
  handleErrorUpdate,
  autoFocus = false,
  shown = true,
  required = true,
  disableFuture = true,
  handleClose = () => {},
}) {
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [otherErrorMessage, setOtherErrorMessage] = useState('');
  const [autoOpen, setAutoOpen] = useState(false);

  const setDate = (newValue) => {
    // validate date is between min and max then handle date change
    // otherwise set error
    // If minDate is explicitly null, don't enforce a minimum (allow any past date)
    // Otherwise use the provided minDate or default to 15 years ago
    const effectiveMinDate =
      minDate != null ? minDate : dayjs().subtract(15, 'year');
    const effectiveMaxDate = maxDate != null ? maxDate : dayjs();

    // Check if date is >= minDate (only if minDate is provided) and <= maxDate (inclusive)
    const isAfterOrEqualMin =
      minDate == null || !newValue.isBefore(effectiveMinDate);
    const isBeforeOrEqualMax = !newValue.isAfter(effectiveMaxDate);

    if (isAfterOrEqualMin && isBeforeOrEqualMax) {
      setError(false);
      setErrorMessage('');
      handleErrorUpdate(false);
      handleDateChange(newValue);
    } else {
      handleErrorUpdate(true);
      setError(true);
      if (minDate != null && maxDate != null) {
        setErrorMessage(
          `Date must be between ${minDate.format('MM-DD-YYYY')} and ${maxDate.format('MM-DD-YYYY')}`
        );
      } else if (maxDate != null) {
        setErrorMessage(
          `Date must be on or before ${maxDate.format('MM-DD-YYYY')}`
        );
      } else {
        setErrorMessage('Date must be in the past');
      }
    }
  };

  useEffect(() => {
    if (extraError) {
      setError(true);
      setOtherErrorMessage(extraErrorMessage);
    } else {
      setError(false);
      setOtherErrorMessage('');
    }
  }, [extraError, extraErrorMessage]);

  const checkOpen = useCallback(() => {
    if (autoFocus && shown) {
      setAutoOpen(true);
    }
  }, [autoFocus, shown]);

  const handleCloseDate = () => {
    setAutoOpen(false);
    handleClose(date);
  };

  useEffect(() => {
    checkOpen();
  }, [checkOpen]);

  return (
    <DatePicker
      value={date}
      label={label}
      open={autoOpen}
      disableFuture={disableFuture}
      onOpen={() => setAutoOpen(true)}
      onClose={() => handleCloseDate()}
      openTo="year"
      views={['year', 'month', 'day']}
      onError={(newError) => setError(newError)}
      slotProps={{
        textField: {
          helperText: errorMessage || otherErrorMessage,
          error: !!error,
          fullWidth: true,
          size: 'small',
          required: required,
        },
      }}
      sx={{
        '& .MuiInputBase-root': {
          height: 40, // Adjust the height as needed
        },
        '& .MuiOutlinedInput-input': {
          fontSize: 16, // Adjust the font size as needed
        },
      }}
      minDate={minDate != null ? minDate : dayjs().subtract(15, 'year')}
      maxDate={maxDate != null ? maxDate : dayjs()}
      onChange={(newValue) => setDate(newValue)}
    />
  );
}
